import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  TextField,
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

const SymptomsForm = ({ symptoms, setSymptoms }) => {
  // Handle both string (old format) and array (new format) symptoms
  const [symptomsList, setSymptomsList] = useState([]);
  const [newSymptom, setNewSymptom] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});

  // Normalize symptoms prop - could be string or array
  useEffect(() => {
    if (Array.isArray(symptoms)) {
      setSymptomsList(symptoms.map(s => typeof s === 'string' ? s : (s.name || s.description || '')));
    } else if (typeof symptoms === 'string' && symptoms.trim() !== '') {
      // Convert string to array by splitting on commas or newlines
      const symptomsArray = symptoms
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(s => s !== '');
      setSymptomsList(symptomsArray);
    } else {
      setSymptomsList([]);
    }
  }, [symptoms]);

  // Sync changes back to parent (but only when symptomsList changes, not when symptoms prop changes)
  const prevSymptomsListRef = React.useRef(symptomsList);
  useEffect(() => {
    // Only update if symptomsList actually changed (not from prop update)
    if (prevSymptomsListRef.current !== symptomsList) {
      prevSymptomsListRef.current = symptomsList;
      if (Array.isArray(symptoms)) {
        // Parent expects array - update it
        const currentArray = Array.isArray(symptoms) ? symptoms : [];
        if (JSON.stringify(currentArray.sort()) !== JSON.stringify(symptomsList.sort())) {
          setSymptoms([...symptomsList]);
        }
      } else {
        // Parent expects string - join array
        const symptomsString = symptomsList.join(', ');
        if (symptoms !== symptomsString) {
          setSymptoms(symptomsString);
        }
      }
    }
  }, [symptomsList]); // Removed setSymptoms from deps to avoid warnings

  const handleAddSymptom = useCallback(() => {
    if (!newSymptom.trim()) {
      setErrors({ symptom: 'Symptom description cannot be empty' });
      return;
    }

    if (editingIndex !== null) {
      const updated = [...symptomsList];
      updated[editingIndex] = newSymptom.trim();
      setSymptomsList(updated);
      setEditingIndex(null);
    } else {
      setSymptomsList([...symptomsList, newSymptom.trim()]);
    }

    setNewSymptom('');
    setErrors({});
  }, [newSymptom, editingIndex, symptomsList]);

  const handleEditSymptom = useCallback((index) => {
    setNewSymptom(symptomsList[index]);
    setEditingIndex(index);
    setErrors({});
  }, [symptomsList]);

  const handleDeleteSymptom = useCallback((index) => {
    const updated = symptomsList.filter((_, i) => i !== index);
    setSymptomsList(updated);
    
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewSymptom('');
      setErrors({});
    }
  }, [symptomsList, editingIndex]);

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    setNewSymptom('');
    setErrors({});
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddSymptom();
    }
  }, [handleAddSymptom]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h6'>Symptoms</Typography>
        {symptomsList.length > 0 && (
          <Chip 
            label={`${symptomsList.length} symptom${symptomsList.length !== 1 ? 's' : ''}`} 
            color="primary" 
            size="small" 
          />
        )}
      </Box>

      {errors.symptom && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors({})}>
          {errors.symptom}
        </Alert>
      )}

      <TextField
        label="Add Symptom"
        fullWidth
        multiline
        rows={2}
        value={newSymptom}
        onChange={(e) => {
          setNewSymptom(e.target.value);
          if (errors.symptom) {
            setErrors({});
          }
        }}
        onKeyPress={handleKeyPress}
        placeholder="Enter symptom description (e.g., Headache, Fever, Fatigue)"
        helperText="Enter a symptom and press Enter or click Add"
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant='contained'
          color='primary'
          startIcon={editingIndex !== null ? <EditIcon /> : <AddIcon />}
          onClick={handleAddSymptom}
        >
          {editingIndex !== null ? 'Update Symptom' : 'Add Symptom'}
        </Button>
        {editingIndex !== null && (
          <Button
            variant='outlined'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </Box>

      {symptomsList.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {symptomsList.map((symptom, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                padding: 2,
                marginBottom: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: editingIndex === index ? '2px solid' : '1px solid',
                borderColor: editingIndex === index ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <Typography variant='body1' sx={{ flex: 1 }}>
                {symptom}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  color='primary'
                  size="small"
                  onClick={() => handleEditSymptom(index)}
                  disabled={editingIndex !== null && editingIndex !== index}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color='error'
                  size="small"
                  onClick={() => handleDeleteSymptom(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant='body2' color="text.secondary">
            No symptoms added yet. Add symptoms above.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SymptomsForm;
